import React, { useEffect, useRef } from 'react';

/**
 * Interactive fluid mesh-gradient background, matching the ImageMotion
 * marketing site. Palette: royal blue, indigo, steel, violet. Mouse-reactive,
 * and a single static frame when the user prefers reduced motion.
 */

interface Blob {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  radius: number;
  baseHue: number;
  hue: number;
  saturation: number;
  lightness: number;
  alpha: number;
  speedX: number;
  speedY: number;
  phase: number;
  phaseSpeed: number;
}

const BLOB_COUNT = 5;

const FluidBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5, active: false });
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Blobs are stretched from a low-resolution canvas, which keeps the mesh
    // soft and cuts per-frame pixel work.
    const SCALE = 0.4;
    const resize = () => {
      canvas.width = Math.max(1, Math.floor(window.innerWidth * SCALE));
      canvas.height = Math.max(1, Math.floor(window.innerHeight * SCALE));
    };
    resize();
    window.addEventListener('resize', resize);

    const onMove = (e: MouseEvent) => {
      mouseRef.current.x = e.clientX / window.innerWidth;
      mouseRef.current.y = e.clientY / window.innerHeight;
      mouseRef.current.active = true;
    };
    const onLeave = () => {
      mouseRef.current.active = false;
    };
    if (!reduceMotion) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseleave', onLeave);
    }

    const palette = [
      { hue: 220, sat: 75, light: 50, alpha: 0.26 },
      { hue: 240, sat: 70, light: 55, alpha: 0.22 },
      { hue: 200, sat: 65, light: 45, alpha: 0.2 },
      { hue: 260, sat: 60, light: 50, alpha: 0.22 },
      { hue: 210, sat: 55, light: 40, alpha: 0.18 },
    ];

    const blobs: Blob[] = [];
    for (let i = 0; i < BLOB_COUNT; i++) {
      const p = palette[i % palette.length];
      blobs.push({
        x: Math.random(),
        y: Math.random(),
        targetX: Math.random(),
        targetY: Math.random(),
        radius: 0.25 + Math.random() * 0.2,
        baseHue: p.hue,
        hue: p.hue,
        saturation: p.sat,
        lightness: p.light,
        alpha: p.alpha,
        speedX: (Math.random() - 0.5) * 0.0003,
        speedY: (Math.random() - 0.5) * 0.0003,
        phase: Math.random() * Math.PI * 2,
        phaseSpeed: 0.003 + Math.random() * 0.004,
      });
    }

    const paintBlob = (blob: Blob, w: number, h: number) => {
      const cx = blob.x * w;
      const cy = blob.y * h;
      const r = blob.radius * Math.max(w, h);
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
      const h1 = ((blob.hue % 360) + 360) % 360;
      grad.addColorStop(0, `hsla(${h1}, ${blob.saturation}%, ${blob.lightness}%, ${blob.alpha})`);
      grad.addColorStop(0.5, `hsla(${h1}, ${blob.saturation}%, ${blob.lightness * 0.7}%, ${blob.alpha * 0.4})`);
      grad.addColorStop(1, `hsla(${h1}, ${blob.saturation}%, ${blob.lightness * 0.5}%, 0)`);
      ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
    };

    const FRAME_MS = 1000 / 30;
    let lastFrame = 0;

    const draw = (now = 0) => {
      rafRef.current = requestAnimationFrame(draw);
      if (now - lastFrame < FRAME_MS) return;
      lastFrame = now;

      const w = canvas.width;
      const h = canvas.height;
      const mouse = mouseRef.current;

      ctx.fillStyle = '#050509';
      ctx.fillRect(0, 0, w, h);

      for (const blob of blobs) {
        blob.phase += blob.phaseSpeed;
        blob.targetX += blob.speedX + Math.sin(blob.phase) * 0.0002;
        blob.targetY += blob.speedY + Math.cos(blob.phase * 0.7) * 0.0002;

        if (blob.targetX < -0.1 || blob.targetX > 1.1) blob.speedX *= -1;
        if (blob.targetY < -0.1 || blob.targetY > 1.1) blob.speedY *= -1;
        blob.targetX = Math.max(-0.2, Math.min(1.2, blob.targetX));
        blob.targetY = Math.max(-0.2, Math.min(1.2, blob.targetY));

        if (mouse.active) {
          const dx = mouse.x - blob.targetX;
          const dy = mouse.y - blob.targetY;
          blob.targetX += dx * 0.008;
          blob.targetY += dy * 0.008;
          const hueShift = (mouse.x - 0.5) * 60 + (mouse.y - 0.5) * 40;
          blob.hue = blob.baseHue + hueShift;
        } else {
          blob.hue += (blob.baseHue - blob.hue) * 0.01;
        }

        blob.x += (blob.targetX - blob.x) * 0.02;
        blob.y += (blob.targetY - blob.y) * 0.02;
        paintBlob(blob, w, h);
      }

      ctx.globalCompositeOperation = 'source-over';
    };

    if (reduceMotion) {
      for (let i = 0; i < 60; i++) {
        for (const blob of blobs) {
          blob.x += (blob.targetX - blob.x) * 0.2;
          blob.y += (blob.targetY - blob.y) * 0.2;
        }
      }
      const w = canvas.width;
      const h = canvas.height;
      ctx.fillStyle = '#050509';
      ctx.fillRect(0, 0, w, h);
      for (const blob of blobs) paintBlob(blob, w, h);
      ctx.globalCompositeOperation = 'source-over';
    } else {
      rafRef.current = requestAnimationFrame(draw);
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-0 h-screen w-screen"
      aria-hidden="true"
    />
  );
};

export default FluidBackground;
